import { useEffect, useMemo, useState, type DragEvent, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import type {
  AthleteDetail,
  StrengthSetResult,
  PlanVersion,
  TrainingCycle,
  TrainingPlan,
  TrainingSession,
  WorkoutTemplate,
} from "../../api/types";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FormError,
  Loading,
  Modal,
  PageHead,
  SelectField,
  TextArea,
  TextField,
  useToast,
} from "../../components/ui";
import { PlanImportModal } from "../../components/PlanImportModal";
import { ProgressLine, StrengthResultsReadOnly } from "../../components/StrengthLog";
import { emptyWorkout, WorkoutForm, type WorkoutFormValue } from "../../components/WorkoutBuilder";
import { formatDateTime, metersToKm } from "../../lib/format";
import {
  addDays,
  CYCLE_LABEL,
  fmtDayHeader,
  fmtLong,
  fmtMonth,
  iso,
  parseIso,
  plannedDistance,
  startOfMonthGrid,
  startOfWeek,
  todayIso,
  weekLabel,
  WORKOUT_TONE,
  WORKOUT_TYPE_LABEL,
} from "../../lib/training";
import { useAsync } from "../../lib/useAsync";
import { Link } from "../../router/router";
import { t, tn, tj } from "../../i18n";

type View = "week" | "month";
type Editing = { kind: "new"; date: string } | { kind: "edit"; session: TrainingSession } | null;

const PUBLICATION_BADGE: Record<TrainingSession["publication"], { tone: "ok" | "warn" | "off"; label: string } | null> = {
  new: { tone: "warn", label: t("Não publicado") },
  changed: { tone: "warn", label: t("Alterado") },
  published: null,
  locked: null,
};

/** Soma da semana: exata, aproximada (≈) ou mínima (≥) conforme a pior qualidade. */
function weekTotal(sessions: TrainingSession[]): string {
  const withDist = sessions.filter((s) => s.planned_distance_m !== null);
  if (!withDist.length) return "";
  const m = withDist.reduce((a, s) => a + (s.planned_distance_m ?? 0), 0);
  const runs = sessions.filter((s) => s.workout_type !== "rest" && s.workout_type !== "strength");
  const partial = runs.some((s) => s.planned_distance_m === null || s.distance_source === "partial");
  const est = withDist.some((s) => s.distance_source === "estimated");
  return (partial ? "≥ " : est ? "≈ " : "") + metersToKm(m);
}

export function PlanPage({ orgId, athleteId }: { orgId: string; athleteId: string }) {
  const toast = useToast();
  const [view, setView] = useState<View>("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [editing, setEditing] = useState<Editing>(null);
  const [templateFor, setTemplateFor] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showCycles, setShowCycles] = useState(false);
  const [copying, setCopying] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);

  const start = view === "week" ? startOfWeek(anchor) : startOfMonthGrid(anchor);
  const days = view === "week" ? 7 : 42;
  const end = addDays(start, days - 1);
  const base = `/orgs/${orgId}/athletes/${athleteId}/plan`;

  const athlete = useAsync(() => api.get<AthleteDetail>(`/orgs/${orgId}/athletes/${athleteId}`), [orgId, athleteId]);
  const plan = useAsync(() => api.get<TrainingPlan>(base), [base]);
  const sessions = useAsync(
    () => api.get<TrainingSession[]>(`${base}/sessions`, { from: iso(start), to: iso(end) }),
    [base, iso(start), iso(end)],
  );
  const reloadAll = () => {
    plan.reload();
    sessions.reload();
  };

  const byDay = useMemo(() => {
    const map: Record<string, TrainingSession[]> = {};
    for (const s of sessions.data ?? []) (map[s.date] ??= []).push(s);
    return map;
  }, [sessions.data]);

  const cycleOf = (day: string): TrainingCycle | undefined =>
    plan.data?.cycles.find((c) => c.start_date <= day && day <= c.end_date);

  async function moveSession(sessionId: string, day: string) {
    const s = sessions.data?.find((x) => x.id === sessionId);
    if (!s || s.date === day) return;
    try {
      await api.patch(`${base}/sessions/${sessionId}`, { date: day });
      toast(t("“{title}” movido para {day}.", { title: s.title, day: fmtLong(day) }));
      reloadAll();
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  function onDrop(e: DragEvent, day: string) {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setDragId(null);
    if (id) moveSession(id, day);
  }

  if (athlete.error) return <ErrorState error={athlete.error} />;
  if (plan.error) return <ErrorState error={plan.error} onRetry={plan.reload} />;
  const pending = plan.data?.pending_changes ?? 0;
  const today = todayIso();
  const step = (dir: number) =>
    setAnchor(view === "week" ? addDays(anchor, 7 * dir) : new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));

  const dayCell = (d: Date, compact: boolean) => {
    const day = iso(d);
    const list = byDay[day] ?? [];
    const cycle = cycleOf(day);
    const outside = view === "month" && d.getMonth() !== anchor.getMonth();
    return (
      <div
        key={day}
        className={`cal-day${day === today ? " is-today" : ""}${outside ? " is-outside" : ""}${cycle ? ` cycle-${cycle.phase}` : ""}${dragId ? " droppable" : ""}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => onDrop(e, day)}
        aria-label={fmtLong(day)}
      >
        <div className="cal-day-head">
          <span className="cal-date">{compact ? d.getDate() : fmtDayHeader(d)}</span>
          {cycle && (cycle.start_date === day || d.getDay() === 1 || day === iso(start)) && (
            <span className="cycle-chip" title={`${CYCLE_LABEL[cycle.phase]}: ${cycle.name}`}>
              {cycle.name}
            </span>
          )}
        </div>
        <div className="cal-items">
          {list.map((s) => (
            <SessionCard
              key={s.id}
              s={s}
              compact={compact}
              onOpen={() => setEditing({ kind: "edit", session: s })}
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", s.id);
                e.dataTransfer.effectAllowed = "move";
                setDragId(s.id);
              }}
              onDragEnd={() => setDragId(null)}
            />
          ))}
        </div>
        <div className="cal-add">
          <button type="button" className="link-btn" onClick={() => setEditing({ kind: "new", date: day })}>
            {t("+ Treino")}
          </button>
          <button type="button" className="link-btn" onClick={() => setTemplateFor(day)}>
            {t("+ Modelo")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <p style={{ marginBottom: 8 }}>
        <Link to={`/org/${orgId}/atletas`}>{t("Atletas")}</Link>
        {athlete.data && (
          <>
            {" / "}
            <Link to={`/org/${orgId}/atletas/${athleteId}`}>{athlete.data.full_name}</Link>
          </>
        )}
      </p>
      <PageHead
        title={athlete.data ? t("Planilha: {full_name}", { full_name: athlete.data.full_name }) : t("Planilha")}
        subtitle={
          plan.data &&
          (plan.data.current_version
            ? t("Versão {current_version} publicada em {last_published_at}.", { current_version: plan.data.current_version, last_published_at: formatDateTime(plan.data.last_published_at) })
            : t("Ainda não publicada: o atleta não vê nenhum treino."))
        }
        actions={
          <>
            <Button onClick={() => setImporting(true)}>{t("Importar planilha")}</Button>
            <Link to={`/org/${orgId}/atletas/${athleteId}/atividades`} className="btn btn-secondary">
              {t("Atividades")}
            </Link>
            <Button onClick={() => setShowCycles(true)}>{t("Ciclos")}</Button>
            <Button onClick={() => setShowVersions(true)} disabled={!plan.data?.current_version}>
              {t("Versões")}
            </Button>
            <Button variant="primary" onClick={() => setPublishing(true)} disabled={pending === 0}>
              {pending ? tn("Publicar {n} alteração", "Publicar {n} alterações", pending) : t("Tudo publicado")}
            </Button>
          </>
        }
      />

      <div className="cal-toolbar">
        <div className="segmented" role="group" aria-label={t("Visualização")}>
          <button type="button" aria-pressed={view === "week"} onClick={() => setView("week")}>
            {t("Semana")}
          </button>
          <button type="button" aria-pressed={view === "month"} onClick={() => setView("month")}>
            {t("Mês")}
          </button>
        </div>
        <div className="cal-nav">
          <Button size="sm" onClick={() => step(-1)} aria-label={t("Período anterior")}>
            ‹
          </Button>
          <Button size="sm" onClick={() => setAnchor(new Date())}>
            {t("Hoje")}
          </Button>
          <Button size="sm" onClick={() => step(1)} aria-label={t("Próximo período")}>
            ›
          </Button>
          <strong className="cal-range">{view === "week" ? weekLabel(start) : fmtMonth(anchor)}</strong>
        </div>
        <div className="cal-extra">
          {view === "week" && sessions.data && (
            <span className="muted">{t("Planejado na semana:")} <strong className="num">{weekTotal(sessions.data) || "—"}</strong></span>
          )}
          {view === "week" && (
            <Button size="sm" onClick={() => setCopying(true)} disabled={!sessions.data?.length}>
              {t("Copiar semana")}
            </Button>
          )}
        </div>
      </div>

      {sessions.error && <ErrorState error={sessions.error} onRetry={sessions.reload} />}
      {!sessions.data && sessions.loading && <Loading />}
      {sessions.data && (
        <div aria-busy={sessions.loading}>
          {view === "week" ? (
            <div className="cal-week">{Array.from({ length: 7 }, (_, i) => dayCell(addDays(start, i), false))}</div>
          ) : (
            <div className="cal-month-wrap">
              <div className="cal-month">
                {[t("Seg"), t("Ter"), t("Qua"), t("Qui"), t("Sex"), t("Sáb"), t("Dom"), t("Semana")].map((d) => (
                  <div key={d} className="cal-month-head">
                    {d}
                  </div>
                ))}
                {Array.from({ length: 6 }, (_, w) => {
                  const ws = addDays(start, w * 7);
                  const weekSessions = Array.from({ length: 7 }, (_, i) => byDay[iso(addDays(ws, i))] ?? []).flat();
                  return [
                    ...Array.from({ length: 7 }, (_, i) => dayCell(addDays(ws, i), true)),
                    <div key={`t${w}`} className="cal-week-total num">
                      {weekTotal(weekSessions)}
                    </div>,
                  ];
                })}
              </div>
            </div>
          )}
          <p className="muted cal-help">
            {t("Arraste um treino para outro dia para remarcar, ou abra o treino e altere a data. Alterações só chegam ao atleta depois de publicar.")}
          </p>
        </div>
      )}

      <SessionModal
        orgId={orgId}
        base={base}
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={(msg) => {
          setEditing(null);
          toast(msg);
          reloadAll();
        }}
      />
      <TemplatePicker
        orgId={orgId}
        date={templateFor}
        onClose={() => setTemplateFor(null)}
        onPick={async (tp) => {
          try {
            await api.post(`${base}/sessions/from-template`, { template_id: tp.id, date: templateFor });
            toast(t("“{name}” adicionado em {date}.", { name: tp.name, date: fmtLong(templateFor!) }));
            setTemplateFor(null);
            reloadAll();
          } catch (err) {
            toast((err as ApiError).message, "error");
          }
        }}
      />
      <PublishModal
        open={publishing}
        base={base}
        pending={pending}
        onClose={() => setPublishing(false)}
        onDone={(v) => {
          setPublishing(false);
          toast(t("Versão {version_no} publicada. O atleta já vê os treinos.", { version_no: v.version_no }));
          reloadAll();
        }}
      />
      <PlanImportModal
        open={importing}
        base={base}
        defaultMonday={iso(addDays(startOfWeek(new Date()), 7))}
        onClose={() => setImporting(false)}
        onApplied={(n, first) => {
          setImporting(false);
          toast(tn("{n} treino criado como rascunho. Revise e publique quando quiser.", "{n} treinos criados como rascunho. Revise e publique quando quiser.", n));
          if (first) setAnchor(parseIso(first));
          reloadAll();
        }}
      />
      <VersionsModal open={showVersions} base={base} onClose={() => setShowVersions(false)} />
      <CyclesModal
        open={showCycles}
        base={base}
        cycles={plan.data?.cycles ?? []}
        onClose={() => setShowCycles(false)}
        onChanged={plan.reload}
      />
      <CopyWeekModal
        open={copying}
        base={base}
        from={start}
        onClose={() => setCopying(false)}
        onDone={(n, to) => {
          setCopying(false);
          toast(tn("{n} treino copiado para a semana de {to}.", "{n} treinos copiados para a semana de {to}.", n, { to: weekLabel(to) }));
          setAnchor(to);
          reloadAll();
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------------ cartão de treino */

function SessionCard({
  s,
  compact,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  s: TrainingSession;
  compact: boolean;
  onOpen: () => void;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
}) {
  const locked = s.publication === "locked";
  const badge = PUBLICATION_BADGE[s.publication];
  const dist = plannedDistance(s.planned_distance_m, s.distance_source);
  return (
    <button
      type="button"
      className={`session-card tone-${WORKOUT_TONE[s.workout_type]}${locked ? " is-locked" : ""}`}
      onClick={onOpen}
      draggable={!locked}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      title={s.summary_text || s.title}
    >
      <span className="sc-title">{s.title}</span>
      {!compact && <span className="sc-type">{WORKOUT_TYPE_LABEL[s.workout_type]}</span>}
      {dist && <span className="sc-dist num">{dist}</span>}
      {!compact && s.summary_text && <span className="sc-summary">{s.summary_text}</span>}
      <span className="sc-badges">
        {badge && <Badge tone={badge.tone}>{badge.label}</Badge>}
        {s.athlete_status === "completed" && (
          <Badge tone="ok">{s.athlete_rpe ? t("Concluído, esforço {rpe}", { rpe: s.athlete_rpe }) : t("Concluído")}</Badge>
        )}
        {s.athlete_status === "skipped" && <Badge tone="danger">{t("Não realizado")}</Badge>}
        {s.strength_progress && s.strength_progress.state !== "none" && (
          <Badge tone={s.strength_progress.state === "complete" ? "ok" : "warn"}>
            {t("Séries {done}/{total}", { done: s.strength_progress.sets_total - s.strength_progress.sets_pending, total: s.strength_progress.sets_total })}
          </Badge>
        )}
        {s.activity && (
          <Badge tone="ok">
            {" "}{t("Realizado {distance_m}", { distance_m: metersToKm(s.activity.distance_m) })}
          </Badge>
        )}
        {!compact && s.source_group && <Badge>{t("Grupo {name}", { name: s.source_group.name })}</Badge>}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ criar / editar sessão */

function toFormValue(s: TrainingSession): WorkoutFormValue {
  return {
    title: s.title,
    workout_type: s.workout_type,
    objective: s.objective ?? "",
    instructions: s.instructions ?? "",
    expected_rpe: s.expected_rpe ? String(s.expected_rpe) : "",
    structure: s.structure,
    strength: s.strength,
    date: s.date,
    start_time: s.start_time ? s.start_time.slice(0, 5) : "",
    internal_notes: s.internal_notes ?? "",
    manual_distance_km:
      s.distance_source === "manual" && s.planned_distance_m !== null ? String(s.planned_distance_m / 1000).replace(".", ",") : "",
  };
}

function SessionResults({ base, sessionId, progress }: { base: string; sessionId: string; progress: NonNullable<TrainingSession["strength_progress"]> }) {
  const res = useAsync(() => api.get<StrengthSetResult[]>(`${base}/sessions/${sessionId}/strength-results`), [base, sessionId, progress.state, progress.sets_pending]);
  return (
    <section className="panel" aria-label={t("Séries realizadas pelo atleta")}>
      <div className="panel-head">
        <h3>{t("Séries realizadas pelo atleta")}</h3>
        <ProgressLine w={{ strength_progress: progress }} />
      </div>
      <div className="panel-body">
        {res.loading && <Loading />}
        {res.error && <ErrorState error={res.error} onRetry={res.reload} />}
        {res.data && <StrengthResultsReadOnly results={res.data} />}
      </div>
    </section>
  );
}

function SessionModal({
  orgId,
  base,
  editing,
  onClose,
  onSaved,
}: {
  orgId: string;
  base: string;
  editing: Editing;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const toast = useToast();
  const [dupDate, setDupDate] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => {
    setConfirmDelete(false);
    setDupDate(editing?.kind === "edit" ? iso(addDays(parseIso(editing.session.date), 7)) : "");
  }, [editing]);
  if (!editing) return <Modal open={false} title="" onClose={onClose}>{null}</Modal>;

  const session = editing.kind === "edit" ? editing.session : null;
  const initial = session ? toFormValue(session) : emptyWorkout(editing.kind === "new" ? editing.date : undefined);

  async function duplicate() {
    if (!session || !dupDate) return;
    try {
      await api.post(`${base}/sessions/${session.id}/duplicate`, { date: dupDate });
      onSaved(t("Treino duplicado para {date}.", { date: fmtLong(dupDate) }));
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  async function remove() {
    if (!session) return;
    try {
      await api.delete(`${base}/sessions/${session.id}`);
      onSaved(t("Treino excluído do rascunho. Publique para remover do calendário do atleta."));
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  return (
    <Modal open title={session ? t("Editar treino") : t("Novo treino: {date}", { date: fmtLong(editing.kind === "new" ? editing.date : "") })} onClose={onClose} wide>
      <WorkoutForm
        key={session?.id ?? (editing.kind === "new" ? editing.date : "")}
        orgId={orgId}
        mode="session"
        initial={initial}
        submitLabel={session ? t("Salvar alterações") : t("Adicionar ao rascunho")}
        onCancel={onClose}
        onSubmit={async (payload) => {
          if (session) {
            await api.patch(`${base}/sessions/${session.id}`, payload);
            onSaved(t("Treino salvo no rascunho."));
          } else {
            await api.post(`${base}/sessions`, payload);
            onSaved(t("Treino adicionado ao rascunho."));
          }
        }}
        notice={
          session && (
            <>
              {session.publication === "locked" && (
                <div className="alert alert-info" role="note">
                  <p>
                    {session.athlete_status === "completed"
                      ? session.athlete_rpe
                        ? t("O atleta já registrou este treino como concluído (esforço {rpe}). Alterações aqui não mudam esse histórico.", { rpe: session.athlete_rpe })
                        : t("O atleta já registrou este treino como concluído. Alterações aqui não mudam esse histórico.")
                      : t("O atleta já registrou este treino como não realizado. Alterações aqui não mudam esse histórico.")}
                  </p>
                  {session.athlete_note && <p>{t("Comentário do atleta: “{athlete_note}”", { athlete_note: session.athlete_note })}</p>}
                </div>
              )}
              {session.strength_progress && session.strength_progress.state !== "none" && (
                <SessionResults base={base} sessionId={session.id} progress={session.strength_progress} />
              )}
              <p className="muted" style={{ fontSize: "var(--step--1)" }}>
                {t("Criado por {name} em {date}", { name: session.created_by?.name ?? "—", date: formatDateTime(session.created_at) })}
                {session.updated_by && t("; última alteração por {name} em {updated_at}", { name: session.updated_by.name, updated_at: formatDateTime(session.updated_at) })}.
              </p>
            </>
          )
        }
        footerExtra={
          session && (
            <div className="session-tools">
              <div className="input-group" style={{ maxWidth: 260 }}>
                <input className="input" type="date" aria-label={t("Data da cópia")} value={dupDate} onChange={(e) => setDupDate(e.target.value)} />
                <Button size="sm" onClick={duplicate}>
                  {t("Duplicar")}
                </Button>
              </div>
              {confirmDelete ? (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                  <span className="muted">{t("Excluir este treino?")}</span>
                  <Button size="sm" variant="danger" onClick={remove}>
                    {t("Sim, excluir")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                    {t("Não")}
                  </Button>
                </span>
              ) : (
                <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
                  {t("Excluir")}
                </Button>
              )}
            </div>
          )
        }
      />
    </Modal>
  );
}

/* ------------------------------------------------------------------ escolher modelo */

function TemplatePicker({
  orgId,
  date,
  onClose,
  onPick,
}: {
  orgId: string;
  date: string | null;
  onClose: () => void;
  onPick: (tp: WorkoutTemplate) => void;
}) {
  const list = useAsync(
    () => (date ? api.get<WorkoutTemplate[]>(`/orgs/${orgId}/workout-templates`) : Promise.resolve([])),
    [orgId, date],
  );
  const [q, setQ] = useState("");
  const items = (list.data ?? []).filter((tp) => tp.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal open={date !== null} title={date ? t("Aplicar modelo: {date}", { date: fmtLong(date) }) : ""} onClose={onClose}>
      {list.loading && <Loading />}
      {list.data && list.data.length === 0 && (
        <EmptyState title={t("Nenhum modelo cadastrado")} action={<Link to={`/org/${orgId}/modelos`}>{t("Criar modelos de treino")}</Link>}>
          {t("Modelos guardam treinos que você usa com frequência.")}
        </EmptyState>
      )}
      {list.data && list.data.length > 0 && (
        <div className="stack" style={{ gap: 10 }}>
          <TextField label={t("Buscar modelo")} type="search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          <ul className="pick-list">
            {items.map((tp) => (
              <li key={tp.id}>
                <button type="button" onClick={() => onPick(tp)}>
                  <strong>{tp.name}</strong>
                  <span className="muted">
                    {WORKOUT_TYPE_LABEL[tp.workout_type]}
                    {tp.totals.distance_m !== null && `, ${plannedDistance(tp.totals.distance_m, tp.totals.distance_quality)}`}
                  </span>
                  {tp.totals.summary_text && <span className="sc-summary">{tp.totals.summary_text}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ publicar */

function PublishModal({
  open,
  base,
  pending,
  onClose,
  onDone,
}: {
  open: boolean;
  base: string;
  pending: number;
  onClose: () => void;
  onDone: (v: PlanVersion) => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    if (open) {
      setNote("");
      setError(null);
    }
  }, [open]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await api.post<PlanVersion>(`${base}/publish`, { note: note || null }));
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      title={t("Publicar planilha")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" type="submit" form="publish-form" busy={busy}>
            {t("Publicar")}
          </Button>
        </>
      }
    >
      <form id="publish-form" className="form" onSubmit={submit}>
        {error && <FormError error={error} />}
        <p>
          {tn(
            "{n} alteração será enviada ao calendário do atleta e uma nova versão ficará registrada no histórico. Treinos que o atleta já marcou como concluídos ou não realizados não são alterados.",
            "{n} alterações serão enviadas ao calendário do atleta e uma nova versão ficará registrada no histórico. Treinos que o atleta já marcou como concluídos ou não realizados não são alterados.",
            pending,
          )}
        </p>
        <TextArea label={t("Nota da versão (opcional)")} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
          placeholder={t("Ex.: Semana de recuperação após a prova")} />
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ versões */

function VersionsModal({ open, base, onClose }: { open: boolean; base: string; onClose: () => void }) {
  const list = useAsync(() => (open ? api.get<PlanVersion[]>(`${base}/versions`) : Promise.resolve([])), [open, base]);
  const [selected, setSelected] = useState<PlanVersion | null>(null);
  useEffect(() => setSelected(null), [open]);
  const summary = (v: PlanVersion) =>
    [
      v.summary.added && tn("{n} novo", "{n} novos", v.summary.added),
      v.summary.updated && tn("{n} alterado", "{n} alterados", v.summary.updated),
      v.summary.removed && tn("{n} removido", "{n} removidos", v.summary.removed),
      v.summary.kept_history && tn("{n} mantido por já ter registro do atleta", "{n} mantidos por já terem registro do atleta", v.summary.kept_history),
    ]
      .filter(Boolean)
      .join(", ");
  return (
    <Modal open={open} title={selected ? t("Versão {version_no}", { version_no: selected.version_no }) : t("Histórico de versões")} onClose={onClose} wide
      footer={selected && <Button onClick={() => setSelected(null)}>{t("Voltar ao histórico")}</Button>}>
      {list.loading && <Loading />}
      {list.error && <ErrorState error={list.error} />}
      {!selected && list.data && (
        <ol className="version-list">
          {list.data.map((v) => (
            <li key={v.version_no}>
              <button type="button" onClick={() => api.get<PlanVersion>(`${base}/versions/${v.version_no}`).then(setSelected)}>
                <strong>{t("Versão {version_no}", { version_no: v.version_no })}</strong>
                <span className="muted">
                  {t("{date} por {name}", { date: formatDateTime(v.published_at), name: v.published_by?.name ?? "—" })}
                </span>
                <span>{summary(v) || t("Sem mudanças de conteúdo")}</span>
                {v.note && <span className="muted">“{v.note}”</span>}
              </button>
            </li>
          ))}
        </ol>
      )}
      {selected && (
        <div className="stack">
          <p className="muted">
            {" "}{t("Conteúdo exatamente como foi publicado em {published_at}. Versões publicadas não podem ser alteradas.", { published_at: formatDateTime(selected.published_at) })}{" "}</p>
          {(selected.snapshot ?? []).length === 0 ? (
            <p className="muted">{t("Nenhum treino nesta versão.")}</p>
          ) : (
            <div className="table-wrap">
              <table className="data responsive">
                <thead>
                  <tr>
                    <th>{t("Data")}</th>
                    <th>{t("Treino")}</th>
                    <th>{t("Estrutura")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.snapshot ?? []).map((s, i) => (
                    <tr key={i}>
                      <td style={{ whiteSpace: "nowrap" }}>{fmtLong(s.date)}</td>
                      <td data-label={t("Treino")}>{s.title}</td>
                      <td data-label={t("Estrutura")} className="muted">
                        {s.summary_text || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ ciclos */

function CyclesModal({
  open,
  base,
  cycles,
  onClose,
  onChanged,
}: {
  open: boolean;
  base: string;
  cycles: TrainingCycle[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const empty = { phase: "base", name: "", start_date: "", end_date: "", goal: "" };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(empty);
      setEditingId(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k: keyof typeof empty) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { ...form, goal: form.goal || null };
      if (editingId) await api.put(`${base}/cycles/${editingId}`, body);
      else await api.post(`${base}/cycles`, body);
      toast(editingId ? t("Ciclo atualizado.") : t("Ciclo criado."));
      setForm(empty);
      setEditingId(null);
      onChanged();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: TrainingCycle) {
    try {
      await api.delete(`${base}/cycles/${c.id}`);
      toast(t("Ciclo “{name}” excluído.", { name: c.name }));
      onChanged();
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  const f = error?.fieldErrors() ?? {};
  return (
    <Modal open={open} title={t("Ciclos de treinamento")} onClose={onClose} wide>
      <div className="stack">
        <p className="muted">
          {t("Os ciclos organizam a preparação (base, desenvolvimento, especificidade, polimento, recuperação). Eles são configurados por você para cada atleta e aparecem no calendário; nenhuma periodização é aplicada automaticamente.")}
        </p>
        {cycles.length > 0 && (
          <div className="table-wrap">
            <table className="data responsive">
              <thead>
                <tr>
                  <th>{t("Ciclo")}</th>
                  <th>{t("Fase")}</th>
                  <th>{t("Período")}</th>
                  <th className="right">{t("Ações")}</th>
                </tr>
              </thead>
              <tbody>
                {cycles.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span className={`cycle-dot cycle-${c.phase}`} aria-hidden /> <strong>{c.name}</strong>
                      {c.goal && <span className="sub">{c.goal}</span>}
                    </td>
                    <td data-label={t("Fase")}>{CYCLE_LABEL[c.phase]}</td>
                    <td data-label={t("Período")}>
                      {fmtLong(c.start_date)} a {fmtLong(c.end_date)}
                    </td>
                    <td className="right" style={{ whiteSpace: "nowrap" }}>
                      <Button size="sm" variant="ghost" onClick={() => {
                        setEditingId(c.id);
                        setForm({ phase: c.phase, name: c.name, start_date: c.start_date, end_date: c.end_date, goal: c.goal ?? "" });
                      }}>
                        {t("Editar")}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => remove(c)}>
                        {t("Excluir")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <form className="form panel panel-body" onSubmit={submit} noValidate>
          <h3>{editingId ? t("Editar ciclo") : t("Novo ciclo")}</h3>
          {error && error.code !== "validation_error" && <FormError error={error} />}
          <div className="form-grid">
            <SelectField label={t("Fase")} value={form.phase} onChange={set("phase")}>
              {Object.entries(CYCLE_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </SelectField>
            <TextField label={t("Nome")} value={form.name} onChange={set("name")} error={f.name} placeholder={t("Ex.: Base para a meia")} />
            <TextField label={t("Início")} type="date" value={form.start_date} onChange={set("start_date")} error={f.start_date} />
            <TextField label={t("Fim")} type="date" value={form.end_date} onChange={set("end_date")} error={f.end_date ?? f[""]} />
          </div>
          <TextField label={t("Objetivo do ciclo (opcional)")} value={form.goal} onChange={set("goal")} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {editingId && (
              <Button variant="ghost" onClick={() => {
                setEditingId(null);
                setForm(empty);
              }}>
                {t("Cancelar edição")}
              </Button>
            )}
            <Button type="submit" variant="primary" busy={busy}>
              {editingId ? t("Salvar ciclo") : t("Criar ciclo")}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ copiar semana */

function CopyWeekModal({
  open,
  base,
  from,
  onClose,
  onDone,
}: {
  open: boolean;
  base: string;
  from: Date;
  onClose: () => void;
  onDone: (copied: number, to: Date) => void;
}) {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    if (open) {
      setTarget(iso(addDays(from, 7)));
      setError(null);
    }
  }, [open, from]);
  const to = target ? startOfWeek(parseIso(target)) : null;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!to) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ copied: number }>(`${base}/copy-week`, { from_week_start: iso(from), to_week_start: iso(to) });
      onDone(r.copied, to);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal open={open} title={t("Copiar semana")} onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" type="submit" form="copy-week" busy={busy} disabled={!to}>
            {t("Copiar treinos")}
          </Button>
        </>
      }>
      <form id="copy-week" className="form" onSubmit={submit}>
        {error && <FormError error={error} />}
        <p>
          {tj("Os treinos da semana de {week} serão copiados, mantendo o dia da semana. A semana de destino não é apagada: os treinos são acrescentados ao rascunho.", { week: <strong>{weekLabel(from)}</strong> })}
        </p>
        <TextField label={t("Qualquer dia da semana de destino")} type="date" value={target} onChange={(e) => setTarget(e.target.value)}
          hint={to ? t("Destino: semana de {to}", { to: weekLabel(to) }) : undefined} />
      </form>
    </Modal>
  );
}

